import LoginBox from '@/js/shared/components/loginbox';
import { AuthContext, ConferenceContext } from '@/store';
import {
  Box,
  Button,
  Icon,
  Link as RegularLink,
  Typography,
  useTheme,
} from '@mui/material';
import C from '@shared/constants/general';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { INode, INodeProps } from '@/types/node';
import { useNodeProps } from '@/node-editor/hooks';
import { IContent } from './contract';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

interface OverrideStyles {
  root?: React.CSSProperties;
}

const useStyles = (override?: OverrideStyles) => {
  const theme = useTheme();

  return useMemo(
    () => ({
      root: {
        ...override?.root,
      },
      loginLink: {
        textTransform: 'none' as const,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
      regButton: {
        marginBottom: theme.spacing(1),
        minWidth: '120px',
      },
      loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100px',
      },
      welcomeContainer: {
        textAlign: 'center',
        padding: theme.spacing(2),
      },
      backButton: {
        marginBottom: theme.spacing(1),
      },
    }),
    [override, theme]
  );
};

export const Render = ({
  node: {
    id,
    override,
    content: { btnText, startWithLogin },
  },
}: IProps) => {
  const classes = useStyles(override);
  const { conferenceState } = useContext(ConferenceContext);
  const { authState } = useContext(AuthContext);
  const [showLogin, setShowLogin] = useState(startWithLogin);

  // Memoize computed values to prevent unnecessary re-renders
  const showOnlyLoginBox = useMemo(() => {
    if (!conferenceState.conferenceDetailLoaded) return false;

    const regType = conferenceState.conferenceDetail?.registration_type;
    return (
      regType !== C.CONFERENCE.REG_TYPE.INVITATION &&
      regType !== C.CONFERENCE.REG_TYPE.ANONYMOUS
    );
  }, [
    conferenceState.conferenceDetailLoaded,
    conferenceState.conferenceDetail?.registration_type,
  ]);

  // Safe access to window.LANG with fallbacks - memoized to prevent recreation
  const lang = useMemo(
    () => {
      const windowLang = (window as any).LANG;
      return {
        WELCOME: windowLang?.WELCOME || 'Welcome',
        REGISTER: windowLang?.REGISTER || 'Register',
        OR_LOGIN: windowLang?.OR_LOGIN || 'Or Login',
        BACK: windowLang?.BACK || 'Back',
      };
    },
    [] // Empty dependency array since window.LANG is global
  );

  // Safe access to user data with better fallback
  const userName = useMemo(() => {
    return authState.user?.first_name || authState.user?.email || 'User';
  }, [authState.user?.first_name, authState.user?.email]);

  const startLogin = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setShowLogin(true);
  }, []);

  const goBack = useCallback(() => {
    setShowLogin(false);
  }, []);

  // Early return for loading state with better UX
  if (!conferenceState.conferenceDetailLoaded) {
    return (
      <Box component="div" sx={classes.root} {...useNodeProps(id)}>
        <Box sx={classes.loadingContainer}>
          <Typography variant="body2" color="text.secondary">
            Loading conference details...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Render user logged in state
  const renderLoggedInUser = () => (
    <Box sx={classes.welcomeContainer}>
      <Typography variant="body1" component="p" gutterBottom>
        {lang.WELCOME} {userName}!
      </Typography>
      <Button
        href="/live"
        color="primary"
        variant="contained"
        size="large"
        aria-label={`${btnText} - Go to live session`}
      >
        {btnText}
      </Button>
    </Box>
  );

  // Render registration options
  const renderRegistrationOptions = () => (
    <Box sx={classes.welcomeContainer}>
      <Button
        sx={classes.regButton}
        variant="contained"
        color="primary"
        href="/register"
        size="large"
        aria-label="Register for the conference"
      >
        {lang.REGISTER}
      </Button>
      <br />
      <RegularLink
        href="#"
        color="primary"
        sx={classes.loginLink}
        onClick={startLogin}
        aria-label="Login to your account"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            startLogin(e as any);
          }
        }}
      >
        {lang.OR_LOGIN}
      </RegularLink>
    </Box>
  );

  // Render login form
  const renderLoginForm = () => (
    <Box sx={classes.welcomeContainer}>
      {showOnlyLoginBox && (
        <Box sx={classes.backButton}>
          <Button
            startIcon={<Icon>arrow_left</Icon>}
            onClick={goBack}
            size="small"
            aria-label="Go back to registration options"
            color="inherit"
          >
            {lang.BACK}
          </Button>
        </Box>
      )}
      <LoginBox />
    </Box>
  );

  return (
    <Box
      component="div"
      sx={classes.root}
      {...useNodeProps(id)}
      role="navigation"
      aria-label="User authentication"
    >
      {authState.userLoggedIn
        ? renderLoggedInUser()
        : showOnlyLoginBox && !showLogin
        ? renderRegistrationOptions()
        : renderLoginForm()}
    </Box>
  );
};
